import debounce from 'lodash/debounce';
import noop from 'lodash/noop';
import { useEffect, useState } from 'react';

import {
  BaseOption,
  MultiFilterSelect,
  MultiSelectComponentProps,
  MultiSelectProps,
} from 'ui/selects';

import { WORKFLOW_ACTIONS } from 'modules/workflows/constants';
import { useMultiSubscriptionData } from './useMultiSubscriptionData';

const { MODIFY_CHANNEL_SUBSCRIPTION, MODIFY_MESSAGE_TYPE } = WORKFLOW_ACTIONS;

interface MultiSubscriptionSelectProps extends MultiSelectComponentProps<number> {
  /** The placeholder in the filter input */
  filterPlaceholder?: string;
  /** The node class for the subscription */
  nodeClass?: string;
}

const MultiSubscriptionSelect = ({
  categoryLabels: originalCategoryLabels,
  filterPlaceholder = 'Search',
  isError = false,
  isDisabled = false,
  nodeClass,
  onSelect = noop,
  options: originalOptions = [],
  placeholder = 'Select an item',
  defaultValues = [],
}: MultiSubscriptionSelectProps) => {
  const [options, setOptions] = useState<BaseOption<number>[]>(originalOptions);
  const [categoryLabels, setCategoryLabels] =
    useState<MultiSelectProps['categoryLabels']>(originalCategoryLabels);
  const { isLoading, search } = useMultiSubscriptionData();

  const handleSearchChange = (searchTerm = '') =>
    search(searchTerm).then(({ channelOptions = [], messageOptions = [], messageCategories }) => {
      if (nodeClass === MODIFY_CHANNEL_SUBSCRIPTION) {
        setOptions(channelOptions);
      } else if (nodeClass === MODIFY_MESSAGE_TYPE) {
        setOptions(messageOptions);
        setCategoryLabels(messageCategories);
      }
    });

  useEffect(() => {
    setOptions(originalOptions);
  }, [originalOptions]);

  useEffect(() => {
    setCategoryLabels(originalCategoryLabels);
  }, [originalCategoryLabels]);

  return (
    <MultiFilterSelect
      categoryLabels={categoryLabels}
      defaultValues={defaultValues}
      disableInternalFiltering
      inputContent={{ filterPlaceholder }}
      isDisabled={isDisabled}
      isError={isError}
      isFullWidth
      isLoading={isLoading}
      isReadOnly={isDisabled}
      onSearchChange={debounce(handleSearchChange, 300)}
      onSelect={onSelect}
      options={options}
      placeholder={placeholder}
    />
  );
};

export default MultiSubscriptionSelect;
